import React from "react";
import { Geo, OnboardingVisitId, VisitBroadcastId, VisitInstanceId } from "../messages";
import { Visit } from "../system/AppState/AppVisitsState";
import { List } from "../utils/List";
import { ClockActionBannerData } from "./ClockActionBanner";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { VisitPendingConfirmationBannerList } from "./VisitPendingConfirmation/VisitPendingConfirmationBannerList";
import { VisitScreenTabsMedflytBanner } from "./VisitsScreen";
import MapSearchAddressBar from "./AvailableVisitLiveMapAddressSearchBar";
import VisitMarker from "./AvailableVisitsMapVisitMarker";
import { CaregiverHomeMapMarker, CaregiverPositionMapMarker } from "./AvailableVisitsLiveMap";
import MapFabs from "./AvailableVisitsMapFabs";
import { AppAbstractModal } from "./Generic/AppModal/AppAbstractModal";
import ComplianceBanner, { ComplianceBannerProps } from "./ComplianceBanner/ComplianceBanner";
import { useMap } from "react-map-gl/maplibre";
import locationUtils from "../locationUtils";
import { LocalDate } from "@js-joda/core";
import styled from "@emotion/styled";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { AppModalAction, ErrorModal } from "./Generic/AppModal/AppModal";
import { canOpenAppSettings, openApplicationSettings } from "../cordova_plugins";

interface Props {
  visits: List<Visit>;
  caregiver: AppCaregiver;
  caregiverPosition: Geo;
  complianceBannerProps: ComplianceBannerProps | null;
  visitsForPendingBanner: List<Visit.Available>;
  finishOnboardingSection: React.ReactNode;
  clockActionBannerProps: ClockActionBannerData | null;
  onCaseSettingsNavigationClick: () => void;
  onVisitClick: (visitBroadcastId: VisitBroadcastId) => void;
  onOnboardingVisitClick: (onboardingVisitId: OnboardingVisitId) => void;
  onConfirmAssignmentClick: (visitId: VisitBroadcastId, isReviewOnly: boolean) => void;
  onClickClockedInBanner: (details: VisitInstanceId) => void;
  onPendingConfirmationVisitClick: (visitId: VisitBroadcastId, isVisitConfirmed: boolean) => void;
}

const AvailableVisitsMapForMedflytCaregiver = (props: Props) => {
  const map = useMap();
  const [showComplianceAlertPopup, setShowComplianceAlertPopup] = React.useState(false);
  const [hasCaregiverLiveLocation, setHasCaregiverLiveLocation] = React.useState(false);
  const [showAllowLocationModal, setShowAllowLocationModal] = React.useState(false);
  const [caregiverLocationPermissions, setCaregiverLocationPermissions] = React.useState<
    "granted" | "prompt_manually" | "unknown" | "prompt" | "prompt-with-rationale" | "denied"
  >("prompt");
  const [caregiverPosition, setCaregiverPosition] = React.useState<Geo>(props.caregiverPosition);

  React.useEffect(() => {
    locationUtils.getLocationPermissionState().then((state) => {
      if (state === "granted") {
        locationUtils
          .getGeoAndAddress()
          .then((location) => {
            setHasCaregiverLiveLocation(true);
            setCaregiverPosition(location.geoLocation);
            setCaregiverLocationPermissions("granted");
          })
          .catch((error) => {
            console.error(
              `Couldn't get the caregiver's current location for map. ${error.message}`
            );

            setCaregiverLocationPermissions("unknown");
          });
      } else {
        setCaregiverLocationPermissions(state);
      }
    });
  }, []);

  const hanleClickHomeFab = () => {
    map.current?.flyTo({
      zoom: 12,
      animate: true,
      duration: 1000,
      center: { ...props.caregiver.addressGeoLocation },
    });
  };

  const handleClickCurrentLocationFab = () => {
    if (caregiverLocationPermissions !== "prompt" && caregiverLocationPermissions !== "granted") {
      handleOpenAllowLocationModal();
    } else {
      if (!hasCaregiverLiveLocation) {
        locationUtils
          .getGeoAndAddress()
          .then((location) => {
            map.current?.flyTo({
              zoom: 12,
              animate: true,
              duration: 1000,
              center: location.geoLocation,
            });

            setHasCaregiverLiveLocation(true);
            setCaregiverPosition(location.geoLocation);
          })
          .catch((error) => {
            console.error(
              `Couldn't get the caregiver's current location for map. ${error.message}`
            );

            setHasCaregiverLiveLocation(false);
            setCaregiverLocationPermissions("unknown");
          });
      } else {
        map.current?.flyTo({
          zoom: 12,
          animate: true,
          duration: 2000,
          center: caregiverPosition,
        });
      }
    }
  };

  const handleOpenAllowLocationModal = () => {
    if (showComplianceAlertPopup) {
      setShowComplianceAlertPopup(false);
    }

    setShowAllowLocationModal(!showAllowLocationModal);
  };

  const handleOpenComplianceAlertPopup = () => {
    if (showAllowLocationModal) {
      setShowAllowLocationModal(false);
    }

    setShowComplianceAlertPopup(!showComplianceAlertPopup);
  };

  const handleClickAllowLocationButton = () => {
    setShowAllowLocationModal(!showAllowLocationModal);
    const canOpenSettings = canOpenAppSettings();

    if (canOpenSettings) {
      openApplicationSettings();
    } else {
      console.error(`Couldn't open settings. Not a native settings available`);
    }
  };

  const filterVisits = (visit: Visit) => {
    return visit.publicVisitBroadcastDetails.endTimeLocal.toLocalDate().isAfter(LocalDate.now());
  };

  return (
    <>
      <TopBannersWrapper>
        {!props.visitsForPendingBanner.isEmpty() ? (
          <VisitPendingConfirmationBannerList
            visits={props.visitsForPendingBanner}
            onConfirmAssignmentClick={props.onConfirmAssignmentClick}
          />
        ) : (
          <VisitScreenTabsMedflytBanner
            finishOnboardingSection={props.finishOnboardingSection}
            clockActionBannerProps={props.clockActionBannerProps}
            onClickClockedInBanner={props.onClickClockedInBanner}
          />
        )}
      </TopBannersWrapper>

      <SearchAddressBarContainer>
        <MapSearchAddressBar />
      </SearchAddressBarContainer>

      <ElevatedZIndexWrapper>
        <CaregiverPositionMapMarker position={caregiverPosition} />
      </ElevatedZIndexWrapper>

      {hasCaregiverLiveLocation ? (
        <ElevatedZIndexWrapper>
          <CaregiverHomeMapMarker position={props.caregiver.addressGeoLocation} />
        </ElevatedZIndexWrapper>
      ) : null}

      {props.visits.filter(filterVisits).map((visit) => {
        return (
          <VisitMarker
            key={`marker-${visit.type}-${visit.id}`}
            visit={visit}
            position={visit.publicVisitBroadcastDetails.location.location}
            isCaregiverOfMedflyt={props.caregiver.isCaregiverOfMedFlyt}
            popupProps={{
              origin: props.caregiver.addressGeoLocation,
              visitIconHeight: 44.25,
              visitIconWidth: 37.5,
              goToVisit: props.onVisitClick,
              goToOnboardingVisit: props.onOnboardingVisitClick,
              goToPendingConfirmationVisit: props.onPendingConfirmationVisitClick,
            }}
          />
        );
      })}

      <MapFabs
        medflytComplianceAlertFabOptions={{
          showMedflytComplianceAlert:
            props.caregiver.shouldShowComplianceBanner && props.complianceBannerProps !== null,
          onClick: () => handleOpenComplianceAlertPopup(),
        }}
        currentPositionFabOptions={{
          onClick: () => handleClickCurrentLocationFab(),
        }}
        homeFabOptions={{
          onClick: () => hanleClickHomeFab(),
        }}
        caseSettingsFabOptions={{
          onClick: () => props.onCaseSettingsNavigationClick(),
        }}
      />

      {props.complianceBannerProps !== null ? (
        <AlertModalWrapper>
          <AppAbstractModal
            onRequestClose={() => setShowComplianceAlertPopup(false)}
            isOpen={showComplianceAlertPopup}
          >
            <ComplianceBanner {...props.complianceBannerProps} />
          </AppAbstractModal>
        </AlertModalWrapper>
      ) : null}

      <AlertModalWrapper>
        <EnableLocationErrorModal
          isOpen={showAllowLocationModal}
          onRequestClose={() => setShowAllowLocationModal(!showAllowLocationModal)}
          onClickOpenSettings={() => handleClickAllowLocationButton()}
        />
      </AlertModalWrapper>
    </>
  );
};

const EnableLocationErrorModal = (props: {
  isOpen: boolean;
  onRequestClose: () => void;
  onClickOpenSettings: () => void;
}) => {
  const actions: AppModalAction[] = [
    {
      key: "open_settings",
      variant: "soft",
      color: "primary",
      children: translate(STRINGS.OPEN_SETTINGS),
      onClick: props.onClickOpenSettings,
    },
    {
      key: "close",
      variant: "link",
      color: "black",
      children: translate(STRINGS.CLOSE),
      onClick: props.onRequestClose,
    },
  ];

  return (
    <ErrorModal
      isOpen={props.isOpen}
      title={translate(STRINGS.PLEASE_ENABLE_LOCATION)}
      message={translate(STRINGS.UNAVAILABLE_LOCATION_SERVICES_MESSAGE)}
      actions={actions}
      onRequestClose={props.onRequestClose}
    />
  );
};

export default AvailableVisitsMapForMedflytCaregiver;

const TopBannersWrapper = styled.div`
  padding: 10px 10px 0px 10px;
  position: sticky;
  top: 0;
  z-index: 15;
`;

const SearchAddressBarContainer = styled.div`
  display: flex;
  position: sticky;
  flex-direction: column;
  z-index: 15;
  margin: 10px;
`;

const AlertModalWrapper = styled.div`
  z-index: 20;
  position: fixed;
`;

const ElevatedZIndexWrapper = styled.div`
  z-index: 10 !important;
`;
